import { useEffect, useMemo, useRef, useState } from "react";
import { WalletProvider, useWallet } from "@solana/wallet-adapter-react";
import { Keypair } from "@solana/web3.js";
import type { Wallet as SuiWallet } from "@mysten/wallet-standard";
import { primaryAction, type FormIssue } from "./action";
import { jupnetChains, pickBest, quoteRound, quoteRounds, type Delivery, type Quote, type QuoteHead, type QuoteReq } from "./api";
import { evmTokenMeta } from "./balances";
import { BASKET_MAX, USD, addable, basketInputs, basketUsd, pairRows, type BasketPart, type BasketRow } from "./basket";
import { CHAINS, CHAIN_LIST, DESTINATION_CHAINS, isStable, isWithdrawal, jupnetRoutes, jupnetSource, unknownJupnetStables, type Chain, type ChainKey, type Family, type JupnetRoute, type Token } from "./chains";
import { discoverEvmWallets, ensureChain, watchEvmAccount, watchEvmChain, type EvmWalletInfo } from "./evm";
import { prettyUnits, rate, short } from "./format";
import { jupnetDecimals } from "./jupnet";
import { keyAfterStorageChange, loadJupnetKey, onJupnetKeyChange } from "./jupnet-key";
import { TYPICAL_CREDIT, assetIdOf, defaultRoute, jupnetAssets, jupnetBlocked, pickRoute, routeOptions, type JupnetAsset } from "./jupnet-receive";
import { PATH_LABEL, PriceChanged, afterPriceChanged, afterRound, formKey, noQuoteMessage, openIntent, quoteFitsForm, type OpenPhase, type QuoteState } from "./open";
import { discoverSuiWallets, signSuiOpen, watchSuiAccount } from "./sui";
import { cleanAmount, fmtPct, friendlyError, maxSpendable, reversed } from "./swap-form";
import { guaranteed } from "./terms";
import { MAX_DECIMALS, parseDecimals, recipientError, toBaseUnits, tokenError } from "./validate";
import { Activity } from "./ui/Activity";
import { useBalances, useStored } from "./ui/hooks";
import { ChainLogo, IconAlert, IconChevron, IconGear, IconInfo, IconKey, IconMonitor, IconMoon, IconSun, IconSwapV, IconWallet, Spinner, TokenOnChain } from "./ui/icons";
import { RoutePicker } from "./ui/RoutePicker";
import { Settings } from "./ui/Settings";
import { TokenPicker, type Picked } from "./ui/TokenPicker";
import { Tracker } from "./ui/Tracker";
import { FAMILY_NAME, Wallets, type EvmConn, type SuiConn } from "./ui/Wallets";

export type DemoConfig = { apiUrl: string; solanaRpc?: string; solanaProgram?: string; jupnetRpc?: string; jupnetProgram?: string };

export function Root({ cfg }: { cfg: DemoConfig }) {
  return (
    <WalletProvider wallets={[]} autoConnect>
      <App cfg={cfg} />
    </WalletProvider>
  );
}

/** How often a quote on screen is refreshed (a solver's quote is live for a few seconds). */
const QUOTE_REFRESH_MS = 4000;
/** How long typing settles before a quote is asked for. */
const QUOTE_DEBOUNCE_MS = 450;
const STORE = "gum-intent-demo:";

type Theme = "system" | "light" | "dark";

function App({ cfg }: { cfg: DemoConfig }) {
  const sol = useWallet();
  const rpcs = useMemo(() => ({ solanaRpc: cfg.solanaRpc, jupnetRpc: cfg.jupnetRpc }), [cfg.solanaRpc, cfg.jupnetRpc]);

  // ---- theme ------------------------------------------------------------------------------
  const [theme, setTheme] = useStored<Theme>(`${STORE}theme`, "system");
  useEffect(() => {
    const el = document.documentElement;
    if (theme === "system") el.removeAttribute("data-theme");
    else el.setAttribute("data-theme", theme);
  }, [theme]);

  // ---- wallets ----------------------------------------------------------------------------
  // the Jupnet key, held in this browser: the Jupnet account and the signer of Jupnet opens
  const [jupnetKey, setJupnetKey] = useState<Keypair | null>(() => loadJupnetKey());
  // whether the key held is in storage: one storage refused lives only here, and no other tab's
  // change may replace it (it would be gone, and the funds and refunds of its account with it)
  const jupnetKeyStored = useRef(jupnetKey !== null);
  const holdJupnetKey = (k: Keypair | null, stored: boolean) => {
    jupnetKeyStored.current = stored;
    setJupnetKey(k);
  };
  useEffect(
    () =>
      onJupnetKeyChange(() =>
        setJupnetKey((held) => {
          const next = keyAfterStorageChange(held, jupnetKeyStored.current, loadJupnetKey());
          if (next !== held) jupnetKeyStored.current = true;
          return next;
        }),
      ),
    [],
  );
  const jupnetAddr = jupnetKey?.publicKey.toBase58();
  const [evmWallets, setEvmWallets] = useState<EvmWalletInfo[]>([]);
  const [evm, setEvm] = useState<EvmConn | null>(null);
  useEffect(() => discoverEvmWallets(setEvmWallets), []);
  // keep the address on the wallet's selected account; none left = disconnected
  const evmProvider = evm?.info.provider;
  useEffect(() => {
    if (!evmProvider) return;
    return watchEvmAccount(evmProvider, (address) => setEvm((c) => (address && c ? { ...c, address } : null)));
  }, [evmProvider]);
  const [evmChainId, setEvmChainId] = useState<number | null>(null);
  useEffect(() => {
    setEvmChainId(null);
    if (!evmProvider) return;
    return watchEvmChain(evmProvider, setEvmChainId);
  }, [evmProvider]);
  const [suiWallets, setSuiWallets] = useState<SuiWallet[]>([]);
  const [sui, setSui] = useState<SuiConn | null>(null);
  useEffect(() => discoverSuiWallets(setSuiWallets), []);
  const suiWallet = sui?.wallet;
  useEffect(() => {
    if (!suiWallet) return;
    return watchSuiAccount(suiWallet, (account) => setSui((c) => (account && c ? { ...c, account } : null)));
  }, [suiWallet]);
  const solAddr = sol.publicKey?.toBase58();
  const [walletsOpen, setWalletsOpen] = useState<{ focus: Family | null } | null>(null);
  const [keyWarning, setKeyWarning] = useState<string | null>(null);

  // ---- api health and Jupnet ---------------------------------------------------------------
  const [solvers, setSolvers] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    const check = () =>
      fetch(`${cfg.apiUrl}/health`)
        .then((r) => r.json())
        .then((h: any) => setSolvers(h?.solvers_connected?.length ?? 0))
        .catch(() => setSolvers(null));
    check();
    const t = setInterval(check, 15_000);
    return () => clearInterval(t);
  }, [cfg.apiUrl]);
  // where Jupnet can be reached from (null while loading, [] when the api offers none), and Jupnet
  // as a source once the api has its program (a mint the presets do not name has its decimals read)
  const [jupnet, setJupnet] = useState<JupnetRoute[] | null>(null);
  const [jupnetSrc, setJupnetSrc] = useState<Chain | null>(null);
  useEffect(() => {
    jupnetChains(cfg.apiUrl)
      .then(async (j) => {
        setJupnet(jupnetRoutes(j));
        const unknown = unknownJupnetStables(j);
        const decimals = unknown.length && cfg.jupnetRpc ? await jupnetDecimals(cfg.jupnetRpc, unknown).catch(() => ({})) : {};
        setJupnetSrc(jupnetSource(j, decimals));
      })
      .catch(() => setJupnet([]));
  }, [cfg.apiUrl, cfg.jupnetRpc]);

  // ---- the form ---------------------------------------------------------------------------
  const [srcKey, setSrcKey] = useState<ChainKey>("arbitrum");
  const [dstKey, setDstKey] = useState<ChainKey>("solana");
  // receive on Jupnet: the fill on `dst` deposits into gum's bank for a Jupnet account
  const [delivery, setDelivery] = useState<Delivery>("direct");
  const deposit = delivery === "gum_deposit";
  const src = srcKey === "jupnet" ? (jupnetSrc ?? CHAINS.jupnet) : CHAINS[srcKey];
  const dst = CHAINS[dstKey];
  const sourceChains = CHAIN_LIST.map((c) => (c.key === "jupnet" ? (jupnetSrc ?? c) : c));
  const route = deposit ? jupnet?.find((r) => r.chain.key === dstKey) : undefined;
  const dstOptions = deposit ? (route?.tokens ?? []) : dst.dstTokens;
  // what can be received on Jupnet, each through routes picked here (`jupnet-receive.ts`):
  // automatically by quote unless the user fixes one
  const assets = useMemo(() => (jupnet ? jupnetAssets(jupnet) : null), [jupnet]);
  const [routeAuto, setRouteAuto] = useState(true);
  // each route's best quote when several were compared (the route chooser shows them)
  const [routeQuotes, setRouteQuotes] = useState<Partial<Record<ChainKey, bigint | null>>>({});
  const [probing, setProbing] = useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  // the direct destination to come back to when "Receive on Jupnet" is switched off
  const lastDirect = useRef<{ chain: ChainKey; token: string } | null>(null);
  const [amount, setAmount] = useState("");
  // Jupnet only: further stablecoins paid alongside the first, a USD basket (`basket.ts`)
  const [extra, setExtra] = useState<{ addr: string; amount: string }[]>([]);
  const [srcTokenAddr, setSrcTokenAddr] = useState(src.srcTokens[0]!.addr);
  const [customSrc, setCustomSrc] = useState({ addr: "", decimals: "18", sym: "TOKEN" });
  const [dstTokenAddr, setDstTokenAddr] = useState(dst.dstTokens[0]!.addr);
  const [customToken, setCustomToken] = useState({ addr: "", decimals: "6", sym: "TOKEN" });
  const [recipient, setRecipient] = useState("");
  const [recipientTouched, setRecipientTouched] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(false);
  const [slippage, setSlippage] = useStored<number>(`${STORE}slippage`, 100);
  const [srcSlippageStored, setSrcSlippage] = useStored<number | null>(`${STORE}src-slippage`, null);
  const srcSlippage = srcSlippageStored ?? undefined;
  const [picker, setPicker] = useState<"src" | "dst" | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // a token picked on a chain stays picked; one the chain does not offer falls back to its first
  const srcToken: Token = useMemo(() => {
    if (srcTokenAddr === "custom") return { sym: customSrc.sym || "TOKEN", addr: customSrc.addr.trim(), decimals: parseDecimals(customSrc.decimals) ?? -1 };
    return src.srcTokens.find((t) => t.addr === srcTokenAddr) ?? src.srcTokens[0]!;
  }, [src, srcTokenAddr, customSrc]);
  const swapping = !isStable(src, srcToken.addr);
  // a USD basket: several Jupnet stablecoins, one open; its amounts read as 6-decimal USD
  // each row with its stablecoin, paired once (a row whose stablecoin is gone is dropped whole)
  const extraRows = src.family === "jupnet" ? pairRows(extra, src.srcTokens) : [];
  const extraTokens = extraRows.map((r) => r.token);
  const basketOn = extraRows.length > 0;
  // the escrow asset: the stable a hosted swap delivers, else the input itself (one of Jupnet's)
  const escrow = basketOn ? USD : swapping ? src.stable : srcToken;
  const dstToken: Token = useMemo(() => {
    // invalid decimals stay invalid (-1): amounts render "—" and the form says why
    if (dstTokenAddr === "custom" && !deposit) return { sym: customToken.sym || "TOKEN", addr: customToken.addr.trim(), decimals: parseDecimals(customToken.decimals) ?? -1 };
    return dstOptions.find((t) => t.addr === dstTokenAddr) ?? dstOptions[0] ?? dst.dstTokens[0]!;
  }, [dst, dstOptions, dstTokenAddr, customToken, deposit]);
  // received on Jupnet: the asset (USDC deposited is unified USD there) and the symbol shown for it
  const asset: JupnetAsset | undefined = deposit ? assets?.find((a) => a.id === assetIdOf(dstToken)) : undefined;
  const recvSym = deposit ? assetIdOf(dstToken) : dstToken.sym;
  const jupnetBlock = deposit ? jupnetBlocked(src) : null;

  // the recipient defaults to the connected wallet of the destination's family; a Jupnet account
  // to the Jupnet key's, which also signs a Jupnet open
  const walletOf = (c: Chain) => (c.family === "evm" ? evm?.address : c.family === "solana" ? solAddr : c.family === "jupnet" ? jupnetAddr : sui?.account.address);
  const recipientFamily: Family = deposit ? "jupnet" : dst.family;
  // receiving on Jupnet: the user pastes their own Jupnet account; the demo never fills in (or
  // makes) one for them. A wallet destination defaults to the connected wallet.
  const ownRecipient = deposit ? undefined : walletOf(dst);
  useEffect(() => {
    if (!recipientTouched) setRecipient(ownRecipient ?? "");
  }, [ownRecipient, recipientTouched]);

  /** Apply a picked chain and token to one side. */
  function pick(side: "src" | "dst", p: Picked) {
    if (side === "src") {
      setSrcKey(p.chain);
      if (p.custom !== undefined) {
        setCustomSrc({ addr: p.custom, decimals: "18", sym: "TOKEN" });
        setSrcTokenAddr("custom");
      } else setSrcTokenAddr(p.token);
      setExtra((x) => (p.chain === "jupnet" ? x.filter((e) => e.addr !== p.token) : []));
    } else {
      setDelivery(p.delivery);
      setDstKey(p.chain);
      // a Jupnet asset picked: its route is automatic again
      if (p.asset) setRouteAuto(true);
      if (p.custom !== undefined) {
        setCustomToken({ addr: p.custom, decimals: "6", sym: "TOKEN" });
        setDstTokenAddr("custom");
      } else setDstTokenAddr(p.token);
    }
  }

  /** "Receive on Jupnet" on: the same asset if Jupnet takes it (USDC is unified USD), else USD, else
   *  the first with a route; off: back to the direct destination it replaced. */
  function toggleJupnet() {
    if (deposit) {
      const kept = lastDirect.current?.chain !== srcKey ? lastDirect.current : null;
      const back = kept ?? { chain: DESTINATION_CHAINS.find((c) => c.enabled && c.key !== srcKey)!.key, token: "" };
      const c = CHAINS[back.chain];
      setDelivery("direct");
      setDstKey(back.chain);
      setDstTokenAddr(c.dstTokens.find((t) => t.addr === back.token)?.addr ?? c.dstTokens[0]!.addr);
      return;
    }
    if (!assets?.length) return;
    lastDirect.current = { chain: dstKey, token: dstTokenAddr };
    const usable = (a: JupnetAsset) => !!defaultRoute(a, src);
    const a = assets.find((x) => x.id === assetIdOf(dstToken) && usable(x)) ?? assets.find((x) => x.id === "USD" && usable(x)) ?? assets.find(usable) ?? assets[0]!;
    const r = defaultRoute(a, src) ?? { chain: a.routes[0]!.chain, token: a.routes[0]!.token };
    setDelivery("gum_deposit");
    setRouteAuto(true);
    setDstKey(r.chain.key);
    setDstTokenAddr(r.token.addr);
  }

  // the route stays usable as the source changes (never the paying chain): else the default one
  useEffect(() => {
    if (!deposit || !asset) return;
    const ok = routeOptions(asset, src).filter((o) => o.ok);
    if (!ok.length || ok.some((o) => o.chain.key === dstKey)) return;
    setDstKey(ok[0]!.chain.key);
    setDstTokenAddr(ok[0]!.token.addr);
    setRouteAuto(true);
  }, [srcKey, deposit, asset?.id, dstKey, jupnetSrc]);

  // a custom EVM token's symbol and decimals, read off its contract
  useEffect(() => {
    if (srcTokenAddr !== "custom" || src.family !== "evm" || tokenError("evm", customSrc.addr)) return;
    evmTokenMeta(src, customSrc.addr.trim()).then((m) => m && setCustomSrc((c) => (c.addr === customSrc.addr ? { ...c, sym: m.sym, decimals: String(m.decimals) } : c)));
  }, [srcTokenAddr, src.key, customSrc.addr]);
  useEffect(() => {
    if (dstTokenAddr !== "custom" || dst.family !== "evm" || tokenError("evm", customToken.addr)) return;
    evmTokenMeta(dst, customToken.addr.trim()).then((m) => m && setCustomToken((c) => (c.addr === customToken.addr ? { ...c, sym: m.sym, decimals: String(m.decimals) } : c)));
  }, [dstTokenAddr, dst.key, customToken.addr]);

  // ---- balances ---------------------------------------------------------------------------
  const user = walletOf(src);
  const [balanceBump, setBalanceBump] = useState(0);
  const balanceTokens = useMemo(() => [srcToken, ...extraTokens], [srcToken, extraTokens.map((t) => t.addr).join()]);
  const srcBalances = useBalances(src.enabled && srcToken.decimals >= 0 && srcToken.addr ? src : null, balanceTokens, user, rpcs, balanceBump);
  const srcBalance = srcBalances[srcToken.addr];
  const dstBalances = useBalances(!deposit && dstToken.decimals >= 0 && dstToken.addr ? dst : null, [dstToken], recipient && !recipientError(dst.family, recipient) ? recipient : undefined, rpcs, balanceBump);
  const dstBalance = deposit ? undefined : dstBalances[dstToken.addr];

  // ---- quotes and the open ----------------------------------------------------------------
  // a quote round and the form it was taken for (`key`): only a quote of the current form opens
  const [quote, setQuote] = useState<QuoteState | null>(null);
  const [quotedAt, setQuotedAt] = useState<number | null>(null);
  const [busy, setBusy] = useState<"quote" | "open" | null>(null);
  // a quote round's error, which the next good round clears; and an open's, which only the user
  // clears (by quoting or opening again): it may say "do not open again, it may still land"
  const [error, setError] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  // bumped when a background refresh ends, so a failed one schedules the next
  const [refreshes, setRefreshes] = useState(0);
  const [log, setLog] = useState<string[]>([]);
  const [phases, setPhases] = useState<OpenPhase[]>([]);
  const [tracking, setTracking] = useState<string | null>(() => new URLSearchParams(location.search).get("intent"));
  const [activityBump, setActivityBump] = useState(0);

  // the current quote round: an input change retires it, so its late frames are ignored
  const rounds = useRef(quoteRounds()).current;
  // a quote already taken for the form about to be set (the best of several Jupnet routes): shown
  // at once rather than asked for again
  const seed = useRef<QuoteState | null>(null);
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    rounds.retire();
    setError(null);
    const s = seed.current;
    seed.current = null;
    if (s && s.key === keyRef.current) {
      seeded.current = s.key;
      setQuote(s);
      setQuotedAt(Date.now());
      return;
    }
    seeded.current = null;
    setQuote(null);
  }, [srcKey, dstKey, amount, srcToken.addr, srcToken.decimals, srcSlippage, dstToken.addr, dstToken.decimals, recipient, delivery, slippage, JSON.stringify(extra)]);

  const amountBase = srcToken.decimals < 0 ? null : toBaseUnits(amount, srcToken.decimals);
  const parts: BasketPart[] = basketOn ? [{ token: srcToken, amount: amountBase }, ...extraRows.map((r) => ({ token: r.token, amount: toBaseUnits(r.amount, r.token.decimals) }))] : [];
  const inputs = basketOn ? basketInputs(parts) : null;
  // a basket's quote is keyed by every stablecoin and amount in it
  const inputKey = basketOn ? JSON.stringify(inputs ?? parts.map((x) => [x.token.addr, x.amount])) : srcToken.addr;
  // everything the open is built from: a quote counts only for the form it was taken for
  const key = formKey({ src: srcKey, dst: dstKey, inputToken: inputKey, amount: amountBase, srcSlippage, dstToken: dstToken.addr, recipient, delivery, slippage });
  const keyRef = useRef(key);
  keyRef.current = key;

  const decimalsMsg = `Decimals must be a whole number from 0 to ${MAX_DECIMALS}`;
  const sErr = srcTokenAddr !== "custom" ? null : (tokenError(src.family, customSrc.addr) ?? (srcToken.decimals < 0 ? decimalsMsg : null));
  const tErr = dstTokenAddr !== "custom" || deposit ? null : (tokenError(dst.family, customToken.addr) ?? (dstToken.decimals < 0 ? decimalsMsg : null));
  const rErr = recipient ? recipientError(recipientFamily, recipient) : null;
  const withdraw = isWithdrawal(src, dst, dstToken.addr, delivery);
  const issue: FormIssue | null = !src.enabled
    ? { kind: "other", message: src.disabledReason ?? `${src.label} is not available` }
    : !dst.enabled
      ? { kind: "other", message: dst.disabledReason ?? `${dst.label} is not available` }
      : srcKey === dstKey
        ? { kind: "other", message: deposit ? "Deposit through another chain" : "Choose another destination chain" }
        : deposit && src.family === "sui"
          ? { kind: "other", message: "Receive on Jupnet from EVM or Solana" }
          : deposit && !route
            ? { kind: "other", message: `Jupnet can't be reached through ${dst.label}` }
            : sErr
              ? { kind: "other", message: "Check the token you pay with" }
              : !amount.trim() || (amountBase === null && /^0*\.?0*$/.test(amount.trim()))
                ? { kind: "amount", message: "Enter an amount" }
                : !amountBase
                  ? { kind: "other", message: `${srcToken.sym} has at most ${srcToken.decimals} decimals` }
                  : basketOn && parts.some((x) => !x.amount)
                    ? { kind: "amount", message: "Enter an amount for each stablecoin" }
                    : basketOn && !inputs
                      ? { kind: "other", message: "Each stablecoin must be worth at least $0.000001" }
                  : tErr
                    ? { kind: "other", message: "Check the token you receive" }
                    : !recipient
                      ? { kind: "recipient", message: deposit ? "Paste your Jupnet address" : "Add a recipient" }
                      : rErr
                        ? { kind: "other", message: "Check the recipient address" }
                        : null;
  const quotable = !issue;

  /** One quote round. `refresh`: keep showing the current quote until the new round is in. */
  async function getQuote(refresh = false) {
    const live = rounds.start();
    const taken = keyRef.current;
    if (!refresh) {
      setBusy("quote");
      setError(null);
      setOpenError(null);
      setQuote(null);
    }
    const req: QuoteReq = {
      src_chain: src.key,
      dst_chain: dst.key,
      ...(inputs ? { inputs } : { input_token: srcToken.addr, input_amount: amountBase! }),
      dst_token: dstToken.addr,
      dst_recipient: recipient,
      ...(swapping && srcSlippage !== undefined ? { src_slippage_bps: srcSlippage } : {}),
      ...(deposit ? { delivery } : {}),
    };
    // receiving on Jupnet with the route automatic: every usable route is quoted, the best kept
    const candidates = !refresh && deposit && routeAuto && asset ? routeOptions(asset, src).filter((o) => o.ok) : [];
    try {
      if (candidates.length > 1) {
        setProbing(true);
        const tried = await Promise.all(
          candidates.map((o) =>
            quoteRound(cfg.apiUrl, { ...req, dst_chain: o.chain.key, dst_token: o.token.addr }).then(
              (r) => ({ o, r, e: null }),
              (e: Error) => ({ o, r: null, e }),
            ),
          ),
        );
        if (!live()) return;
        const amounts = tried.map(({ o, r }) => {
          const b = r && pickBest(r.quotes);
          return { chain: o.chain.key, amount: b ? BigInt(b.dst_amount) : null, decimals: o.token.decimals };
        });
        setRouteQuotes(Object.fromEntries(amounts.map((a) => [a.chain, a.amount])));
        const win = pickRoute(amounts, asset!.home);
        const w = tried.find((x) => x.o.chain.key === win);
        if (!w?.r) {
          if (tried.every((x) => x.e)) throw tried[0]!.e;
          setQuotedAt(Date.now());
          return setError(noQuoteMessage(Math.max(0, ...tried.map((x) => x.r?.head.solvers_asked ?? 0)), tried.flatMap((x) => x.r?.declined ?? [])));
        }
        const wKey = formKey({ src: srcKey, dst: w.o.chain.key, inputToken: inputKey, amount: amountBase, srcSlippage, dstToken: w.o.token.addr, recipient, delivery, slippage });
        const state: QuoteState = { head: w.r.head, quotes: w.r.quotes, done: true, key: wKey };
        if (wKey === taken) {
          setQuote(state);
          setQuotedAt(Date.now());
        } else {
          // the form moves to the winning route; the reset that follows shows this quote
          seed.current = state;
          setDstKey(w.o.chain.key);
          setDstTokenAddr(w.o.token.addr);
        }
        return;
      }
      let head: QuoteHead | undefined;
      const quotes: Quote[] = [];
      const { declined } = await quoteRound(
        cfg.apiUrl,
        req,
        {
          onHead: (h) => {
            head = h;
            if (live() && !refresh) setQuote({ head: h, quotes: [], done: false, key: taken });
          },
          onQuote: (q) => {
            quotes.push(q);
            if (live() && !refresh) setQuote({ head: head!, quotes: [...quotes], done: false, key: taken });
          },
        },
      );
      if (!live() || !quoteFitsForm(taken, keyRef.current)) return;
      const round = head ? { head, quotes, done: true, key: taken } : null;
      setQuote((prev) => afterRound(prev, round, refresh));
      if (quotes.length || !refresh) setQuotedAt(Date.now());
      if (!quotes.length) setError(noQuoteMessage(head?.solvers_asked ?? 0, declined));
      else if (refresh) setError(null);
    } catch (e) {
      if (live()) setError((e as Error).message);
    } finally {
      if (candidates.length > 1) setProbing(false);
      if (!refresh) setBusy((b) => (b === "quote" ? null : b));
      else setRefreshes((n) => n + 1);
    }
  }

  // a complete form is quoted as soon as typing settles
  useEffect(() => {
    if (!quotable || tracking || busy === "open" || seeded.current === key) return;
    const t = setTimeout(() => getQuote(), QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [key, quotable, tracking, routeAuto]);
  // routes compared at another amount or source say nothing about this one
  useEffect(() => setRouteQuotes({}), [srcKey, srcToken.addr, amountBase, asset?.id, srcSlippage]);
  // a quote on screen stays live: re-quoted every QUOTE_REFRESH_MS while nothing is in flight, so
  // the swap builds a quote the user has just seen
  useEffect(() => {
    if (!quote?.done || !quote.quotes.length || !quotable || busy || openError || tracking) return;
    const t = setTimeout(() => {
      if (!document.hidden) getQuote(true);
      else setQuote((q) => (q ? { ...q } : q)); // hidden tab: look again on the next tick
    }, QUOTE_REFRESH_MS);
    return () => clearTimeout(t);
  }, [quote, quotable, busy, openError, refreshes, tracking]);

  async function open() {
    const shown = quote && pickBest(quote.quotes);
    if (!quote || !shown) return;
    const taken = key;
    if (!quoteFitsForm(quote.key, taken)) return setError("the form changed since this quote; get a new one");
    rounds.retire(); // a refresh in flight must not replace the quote being opened
    setBusy("open");
    setError(null);
    setOpenError(null);
    setLog([]);
    setPhases([]);
    try {
      if (!user) throw new Error(`connect your ${FAMILY_NAME[src.family]} for ${src.label}`);
      const o = await openIntent({
        confirmed: { head: quote.head, quote: shown },
        api: cfg.apiUrl,
        src,
        dst,
        inputToken: srcToken,
        dstToken,
        amount: amountBase!,
        ...(inputs ? { inputs: parts.map((x) => ({ token: x.token, amount: x.amount! })) } : {}),
        recipient,
        delivery,
        slippageBps: slippage,
        srcSlippageBps: srcSlippage,
        user,
        evm: evm?.info.provider,
        solana: sol.signTransaction ? { signTransaction: sol.signTransaction, signAllTransactions: sol.signAllTransactions } : undefined,
        solanaRpc: cfg.solanaRpc,
        solanaProgram: cfg.solanaProgram,
        sui: sui ? { sign: (bytes) => signSuiOpen(sui.wallet, sui.account, bytes) } : undefined,
        jupnetKey: jupnetKey ?? undefined,
        jupnetProgram: cfg.jupnetProgram,
        log: (m) => setLog((l) => [...l, m]),
        phase: (p) => setPhases((ps) => (ps.at(-1) === p ? ps : [...ps.filter((x) => x !== p), p])),
      });
      const via = o.path === "bundle" ? " (swap + open as a Jito bundle)" : o.path && o.path !== "single" ? ` (${PATH_LABEL[o.path]})` : "";
      setLog((l) => [...l, `opened intent ${o.intentHash}${via}`]);
      setQuote(null);
      setAmount("");
      setExtra((x) => x.map((e) => ({ ...e, amount: "" })));
      track(o.intentHash);
      setBalanceBump((n) => n + 1);
      setActivityBump((n) => n + 1);
    } catch (e) {
      if (e instanceof PriceChanged) {
        // nothing was signed: show the new quote and let the user decide, if the form is still
        // the one it was taken for (the inputs are locked while an open runs; this is the guard)
        const fmt = (v: bigint) => `${prettyUnits(v, dstToken.decimals)} ${recvSym}`;
        const current = keyRef.current;
        setQuote((kept) => afterPriceChanged(e, taken, current, kept));
        if (quoteFitsForm(taken, current)) setQuotedAt(Date.now());
        setOpenError(`The price changed: you would now receive at least ${fmt(e.now)} (you confirmed ${fmt(e.was)}). Nothing was signed. Review the new quote and swap again.`);
      } else setOpenError((e as Error).message);
    } finally {
      setBusy(null);
      setPhases([]);
    }
  }

  function track(h: string | null) {
    setTracking(h);
    history.replaceState(null, "", h ? `?intent=${h}` : location.pathname);
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  }

  async function switchNetwork() {
    if (!evm || !src.evmChainId) return;
    setOpenError(null);
    try {
      await ensureChain(evm.info.provider, src.evmChainId, src.label, src.addChain);
    } catch (e) {
      setOpenError((e as Error).message);
    }
  }

  const reverse = reversed({ src: srcKey, srcToken: srcToken.addr, dst: dstKey, dstToken: dstToken.addr, delivery }, { jupnetSrc, routes: jupnet });
  function doReverse() {
    if (!reverse) return;
    setSrcKey(reverse.src);
    setSrcTokenAddr(reverse.srcToken);
    setDstKey(reverse.dst);
    setDstTokenAddr(reverse.dstToken);
    setDelivery(reverse.delivery);
    if (reverse.delivery === "gum_deposit") setRouteAuto(true);
    setAmount("");
  }

  const best = quote ? pickBest(quote.quotes) : undefined;
  const ready = !!best && quote?.done === true && quoteFitsForm(quote.key, key);
  const over = (t: Token, base: string | null) => {
    const b = srcBalances[t.addr];
    return !!base && typeof b === "bigint" && BigInt(base) > b;
  };
  const insufficient = over(srcToken, amountBase) ? srcToken.sym : (parts.slice(1).find((x) => over(x.token, x.amount))?.token.sym ?? null);
  const switchTo = src.family === "evm" && evm && evmChainId !== null && evmChainId !== src.evmChainId ? src.label : null;
  const verb = withdraw ? `Withdraw to ${dst.label}` : deposit ? "Swap to Jupnet" : "Swap";
  const action = primaryAction({
    issue,
    connected: !!user,
    walletName: FAMILY_NAME[src.family],
    switchTo,
    insufficient,
    quote: { loading: busy === "quote" || (quotable && !quote && !error), ready, failed: !!error && !best },
    phase: busy === "open" ? (phases.at(-1) ?? "build") : null,
    approveSym: srcToken.sym,
    verb,
  });
  const onAction = () => {
    if (action.kind === "connect") setWalletsOpen({ focus: issue?.kind === "recipient" && user ? recipientFamily : src.family });
    else if (action.kind === "switch") switchNetwork();
    else if (action.kind === "quote") getQuote();
    else if (action.kind === "open") open();
  };
  const floor = best && quote ? guaranteed(quote.head, best, slippage) : undefined;
  const owners = [evm?.address, solAddr, sui?.account.address, jupnetAddr].filter((x): x is string => !!x);
  const connectedFamilies: ChainKey[] = [evm && "ethereum", solAddr && "solana", sui && "sui", jupnetAddr && "jupnet"].filter((x): x is ChainKey => !!x);
  const errShown = openError ?? (error && !busy ? error : null);
  const errNice = errShown ? friendlyError(errShown) : null;
  const locked = busy === "open";

  return (
    <div className="page">
      <header className="top">
        <a className="brand" href={location.pathname} onClick={(e) => (e.preventDefault(), track(null))} aria-label="GUM Solver Intent Model, new swap">
          <span className="brand-mark" aria-hidden>
            <svg viewBox="0 0 32 32" width="26" height="26">
              <defs>
                <linearGradient id="bm" x1="0" y1="0" x2="32" y2="32">
                  <stop stopColor="var(--accent)" />
                  <stop offset="1" stopColor="var(--accent-2)" />
                </linearGradient>
              </defs>
              <rect width="32" height="32" rx="9" fill="url(#bm)" />
              <path d="M9 17.5c0-4.4 3.1-7.5 7.3-7.5 2.2 0 3.9.7 5.2 2l-2 2c-.8-.8-1.9-1.3-3.2-1.3-2.5 0-4.3 2-4.3 4.8s1.8 4.8 4.4 4.8c1.9 0 3.2-.9 3.6-2.5h-3.7v-2.6H23c.1.5.1.9.1 1.4 0 4-2.7 6.4-6.7 6.4-4.3 0-7.4-3.1-7.4-7.5Z" fill="#fff" />
            </svg>
          </span>
          <span>
            GUM <span className="brand-sub">Solver Intent</span>
          </span>
        </a>
        <div className="top-right">
          <span className={`api-status ${solvers ? "up" : solvers === undefined ? "" : "down"}`} title={cfg.apiUrl} role="status">
            <span className="dot" aria-hidden />
            <span className="api-status-text">{solvers === undefined ? "Connecting…" : solvers === null ? "API unreachable" : `${solvers} solver${solvers === 1 ? "" : "s"} online`}</span>
          </span>
          <button className="icon-btn" onClick={() => setTheme(theme === "system" ? "light" : theme === "light" ? "dark" : "system")} aria-label={`Theme: ${theme}. Change theme`} title={`Theme: ${theme}`}>
            {theme === "light" ? <IconSun /> : theme === "dark" ? <IconMoon /> : <IconMonitor />}
          </button>
          <button className={`wallet-btn${owners.length ? " connected" : ""}`} onClick={() => setWalletsOpen({ focus: null })}>
            {owners.length ? (
              <>
                <span className="logo-stack">
                  {connectedFamilies.map((c) => (
                    <ChainLogo key={c} chain={c} size={18} />
                  ))}
                </span>
                <span>{owners.length === 1 ? short(owners[0]!, 4) : `${owners.length} wallets`}</span>
              </>
            ) : (
              <>
                <IconWallet size={16} /> <span>Connect</span>
              </>
            )}
          </button>
        </div>
      </header>

      <main className="main">
        {keyWarning && (
          <div className="alert warn banner" role="alert">
            <IconAlert size={16} />
            <p>{keyWarning}</p>
            <button className="link-btn" onClick={() => setWalletsOpen({ focus: "jupnet" })}>
              Back up
            </button>
          </div>
        )}
        {tracking ? (
          <section className="card swap-card enter" aria-label="Swap progress">
            <Tracker api={cfg.apiUrl} hash={tracking} jupnetSrc={jupnetSrc} onNew={() => track(null)} />
          </section>
        ) : (
          <section className="card swap-card enter" aria-labelledby="swap-h">
            <div className="card-head">
              <h1 id="swap-h">Swap</h1>
              <div className="card-head-right">
                <button className="icon-btn" onClick={() => setSettingsOpen(true)} aria-label={`Settings, max slippage ${fmtPct(slippage)}`} title="Slippage settings">
                  {slippage !== 100 && <span className="chip-mini">{fmtPct(slippage)}</span>}
                  <IconGear />
                </button>
              </div>
            </div>

            {/* locked while an open runs: the quote being opened is bound to these inputs */}
            <fieldset className="form" disabled={locked}>
              <legend className="sr-only">Swap details</legend>
              <div className="panel">
                <div className="panel-top">
                  <label htmlFor="amount">You pay</label>
                  {user && typeof srcBalance === "bigint" && (
                    <span className="balance">
                      Balance {prettyUnits(srcBalance, srcToken.decimals)}
                      <button
                        className="max-btn"
                        disabled={maxSpendable(srcBalance, srcToken, src) === 0n}
                        onClick={() => setAmount(prettyMax(maxSpendable(srcBalance, srcToken, src), srcToken.decimals))}
                        title={srcToken.native ? "Your balance less a little for network fees" : "Your whole balance"}
                      >
                        Max
                      </button>
                    </span>
                  )}
                </div>
                <div className="panel-row">
                  <input
                    id="amount"
                    className={`amount${insufficient ? " bad" : ""}`}
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="0"
                    value={amount}
                    onChange={(e) => setAmount(cleanAmount(e.target.value))}
                    aria-invalid={!!insufficient}
                    aria-describedby={insufficient ? "amount-err" : undefined}
                  />
                  <TokenButton token={srcToken} chain={src} onClick={() => setPicker("src")} label="Choose the token you pay with" />
                </div>
                {insufficient && over(srcToken, amountBase) && (
                  <p id="amount-err" className="field-error">
                    You have {prettyUnits(srcBalance as bigint, srcToken.decimals)} {srcToken.sym} on {src.label}.
                  </p>
                )}
                {src.family === "jupnet" && src.enabled && (
                  <Basket
                    all={src.srcTokens}
                    first={srcToken}
                    rows={extraRows}
                    balances={user ? srcBalances : {}}
                    total={basketOn ? basketUsd(parts) : null}
                    onChange={setExtra}
                  />
                )}
                {srcTokenAddr === "custom" && <CustomFields value={customSrc} onChange={setCustomSrc} error={sErr} family={src.family} />}
              </div>

              <div className="flip-wrap">
                <button className="flip" onClick={doReverse} disabled={!reverse} aria-label="Reverse direction" title={reverse ? "Reverse direction" : "This route has no reverse"}>
                  <IconSwapV size={18} />
                </button>
              </div>

              <div className="panel">
                <div className="panel-top">
                  <span>You receive{deposit ? " on Jupnet" : ""}</span>
                  {typeof dstBalance === "bigint" && <span className="balance">Balance {prettyUnits(dstBalance, dstToken.decimals)}</span>}
                </div>
                <div className="panel-row">
                  <output className={`amount out${busy === "quote" && !best ? " loading" : ""}`} aria-live="polite" aria-label="Estimated amount you receive">
                    {best && dstToken.decimals >= 0 ? prettyUnits(best.dst_amount, dstToken.decimals) : busy === "quote" || (quotable && !quote && !error) ? <span className="skeleton line wide" /> : <span className="placeholder">0</span>}
                  </output>
                  <TokenButton token={dstToken} sym={recvSym} chain={dst} deposit={deposit} onClick={() => setPicker("dst")} label="Choose the token you receive" />
                </div>
                {best && floor !== undefined && (
                  <p className="panel-sub">
                    At least {prettyUnits(floor, dstToken.decimals)} {recvSym}
                    {quote?.head.source_swap ? ", however the swap fills" : ""}
                  </p>
                )}
                {dstTokenAddr === "custom" && !deposit && <CustomFields value={customToken} onChange={setCustomToken} error={tErr} family={dst.family} />}
                {deposit && jupnetBlock ? (
                  <p className="field-error">{jupnetBlock}</p>
                ) : (
                  deposit && (
                    <div className="route-hint">
                      <span className="route-hint-via">
                        via <ChainLogo chain={dst.key} size={14} /> {dst.label.replace(" Chain", "")}
                      </span>
                      <span className="muted">
                        {probing
                          ? "· comparing routes…"
                          : asset && asset.routes.length === 1
                            ? "· the only route"
                          : routeAuto
                            ? Object.keys(routeQuotes).length > 1
                              ? `· best of ${Object.keys(routeQuotes).length} routes`
                              : "· auto"
                            : "· your pick"}
                      </span>
                      {asset && asset.routes.length > 1 && (
                        <>
                          <span className="muted" aria-hidden>
                            ·
                          </span>
                          <button className="link-btn" onClick={() => setRouteOpen(true)} aria-label={`Change the route to Jupnet, now via ${dst.label}`}>
                            Change
                          </button>
                        </>
                      )}
                    </div>
                  )
                )}
              </div>

              {/* where the funds go: a wallet on the destination chain, or the user's Jupnet account */}
              <div className="dest-choice" role="group" aria-label="Send to">
                <span className="muted">Send to</span>
                <div className="seg">
                  <button type="button" className={deposit ? "" : "on"} aria-pressed={!deposit} onClick={() => deposit && toggleJupnet()}>
                    Wallet
                  </button>
                  <button
                    type="button"
                    className={deposit ? "on" : ""}
                    aria-pressed={deposit}
                    onClick={() => !deposit && toggleJupnet()}
                    disabled={!deposit && !assets?.length}
                    title={assets === null ? "Loading Jupnet routes…" : !assets?.length && !deposit ? "The api offers no route to Jupnet" : "Receive in your Jupnet account"}
                  >
                    <ChainLogo chain="jupnet" size={16} />
                    Jupnet account
                  </button>
                </div>
              </div>
              <Recipient
                value={recipient}
                own={ownRecipient}
                touched={recipientTouched}
                editing={editingRecipient || (!recipient && !ownRecipient) || !!rErr}
                family={recipientFamily}
                label={deposit ? "Jupnet account" : `Recipient on ${dst.label}`}
                error={rErr}
                onEdit={() => setEditingRecipient(true)}
                onChange={(v) => {
                  setRecipientTouched(true);
                  setEditingRecipient(true);
                  setRecipient(v.trim());
                }}
                onReset={() => {
                  setRecipientTouched(false);
                  setEditingRecipient(false);
                }}
                onConnect={() => setWalletsOpen({ focus: recipientFamily })}
              />

              {(swapping || withdraw || (deposit && !best) || src.family === "jupnet") && (
                <p className="info">
                  <IconInfo size={15} />
                  <span>
                    {swapping
                      ? `Your ${srcToken.sym} is swapped to ${src.stable.sym} via ${src.family === "evm" ? "OKX" : "Jupiter"} in the same transaction. A refund comes back as ${src.stable.sym}.`
                      : withdraw
                        ? `Withdraw: your ${srcToken.name ?? srcToken.sym} on Jupnet arrives as ${dstToken.sym} on ${dst.label}.`
                        : deposit
                          ? `A solver deposits into gum's bank on ${dst.label}; Jupnet credits your account, usually within a minute.`
                          : "No gas needed: the api pays the Jupnet fee and your Jupnet key signs."}
                  </span>
                </p>
              )}
            </fieldset>

            {quote && best && (
              <div className={`quote-box${detailsOpen ? " open" : ""}`}>
                {deposit && (
                  <div className="jupnet-receive">
                    <p className="jupnet-receive-head">
                      <ChainLogo chain="jupnet" size={18} />
                      <span>
                        You'll receive ≈{" "}
                        <strong>
                          {prettyUnits(best.dst_amount, dstToken.decimals)} {recvSym}
                        </strong>{" "}
                        on Jupnet
                      </span>
                    </p>
                    <ol className="mini-steps" aria-label="How it arrives">
                      <li>
                        <ChainLogo chain={src.key} size={14} /> Locked on {src.label}
                      </li>
                      <li>
                        <ChainLogo chain={dst.key} size={14} /> Deposited on {dst.label} by a solver
                      </li>
                      <li>
                        <ChainLogo chain="jupnet" size={14} /> Credited on Jupnet <span className="muted">· {TYPICAL_CREDIT}</span>
                      </li>
                    </ol>
                  </div>
                )}
                <button className="quote-summary" onClick={() => setDetailsOpen(!detailsOpen)} aria-expanded={detailsOpen} aria-controls="quote-details">
                  <span>
                    {basketOn
                      ? `1 USD ≈ ${rate(quote.head.gross, USD.decimals, best.dst_amount, dstToken.decimals) ?? "—"} ${recvSym}`
                      : `1 ${srcToken.sym} ≈ ${rate(amountBase ?? "0", srcToken.decimals, best.dst_amount, dstToken.decimals) ?? "—"} ${recvSym}`}
                  </span>
                  <span className="quote-summary-right">
                    <RefreshRing key={quotedAt ?? 0} paused={!!(busy || openError)} />
                    <IconChevron size={16} className="chev" />
                  </span>
                </button>
                {detailsOpen && (
                  <dl id="quote-details" className="quote-details">
                    <div>
                      <dt>Minimum received</dt>
                      <dd>
                        {prettyUnits(floor!, dstToken.decimals)} {recvSym} <span className="muted">· {fmtPct(slippage)} slippage</span>
                      </dd>
                    </div>
                    {quote.head.source_swap && (
                      <div>
                        <dt>Swap via {quote.head.source_swap.aggregator}</dt>
                        <dd>
                          ≈ {prettyUnits(quote.head.source_swap.expected_out, src.stable.decimals)} {src.stable.sym}
                          <span className="muted">
                            {" "}
                            · at least {prettyUnits(quote.head.source_swap.min_out, src.stable.decimals)} ({fmtPct(quote.head.source_swap.slippage_bps)})
                          </span>
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt>Protocol fee</dt>
                      <dd>
                        {prettyUnits(quote.head.protocol_fee, escrow.decimals)} {escrow.sym} <span className="muted">· {fmtPct(quote.head.protocol_fee_bps)}</span>
                      </dd>
                    </div>
                    {BigInt(quote.head.builder_fee) > 0n && (
                      <div>
                        <dt>Builder fee</dt>
                        <dd>
                          {prettyUnits(quote.head.builder_fee, escrow.decimals)} {escrow.sym} <span className="muted">· {fmtPct(quote.head.builder_fee_bps)}</span>
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt>Solver receives</dt>
                      <dd>
                        {prettyUnits(quote.head.net, escrow.decimals)} {escrow.sym}
                      </dd>
                    </div>
                    <div>
                      <dt>Route</dt>
                      <dd className="route-line">
                        <ChainLogo chain={src.key} size={14} /> {src.label}
                        {swapping && <span className="muted"> ({srcToken.sym} → {src.stable.sym})</span>} → <ChainLogo chain={dst.key} size={14} /> {dst.label}
                        {deposit && (
                          <>
                            {" "}
                            → <ChainLogo chain="jupnet" size={14} /> Jupnet
                          </>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Best of</dt>
                      <dd>
                        {quote.quotes.length} quote{quote.quotes.length === 1 ? "" : "s"} from {quote.head.solvers_asked} solver{quote.head.solvers_asked === 1 ? "" : "s"} <span className="muted">· {best.solver}</span>
                      </dd>
                    </div>
                    {quote.quotes.length > 1 && (
                      <ul className="alt-quotes">
                        {[...quote.quotes]
                          .sort((a, b) => (BigInt(b.dst_amount) > BigInt(a.dst_amount) ? 1 : -1))
                          .map((q) => (
                            <li key={q.solver + q.dst_amount}>
                              <span>{q.solver}</span>
                              <span>
                                {prettyUnits(q.dst_amount, dstToken.decimals)} {recvSym}
                              </span>
                            </li>
                          ))}
                      </ul>
                    )}
                    <div>
                      <dt>If unfilled</dt>
                      <dd>
                        Refunded {basketOn ? "in the same stablecoins" : `in ${escrow.sym}`} after {quote.head.validity_seconds} s
                      </dd>
                    </div>
                    <p className="fine">You swap exactly this quote. If it lapses before the build, a fresh one is used only when it guarantees at least as much; otherwise you see the new price first.</p>
                  </dl>
                )}
              </div>
            )}

            <button className={`btn primary block cta${action.busy ? " busy" : ""}`} disabled={action.disabled} onClick={onAction} aria-busy={action.busy}>
              {action.busy && <Spinner size={18} />}
              <span>{action.label}</span>
            </button>

            {locked && phases.length > 0 && <OpenSteps phases={phases} sym={srcToken.sym} />}

            {errNice && (
              <div className="alert error" role="alert">
                <IconAlert size={16} />
                <div>
                  <p>{errNice.title}</p>
                  {errNice.detail && (
                    <details>
                      <summary>Details</summary>
                      <p className="mono small">{errNice.detail}</p>
                    </details>
                  )}
                </div>
              </div>
            )}
            {log.length > 0 && (
              <details className="log">
                <summary>Technical log</summary>
                <ol>
                  {log.map((l, i) => (
                    <li key={i}>{l}</li>
                  ))}
                </ol>
              </details>
            )}
          </section>
        )}

        <Activity api={cfg.apiUrl} owners={owners} jupnetSrc={jupnetSrc} current={tracking} bump={activityBump} onOpen={track} />
      </main>

      <footer className="foot">
        <span>Cross-chain swaps settled by GUM Solver Intent Model: your funds are locked on the source chain until a solver delivers, or refunded.</span>
      </footer>

      {picker && (
        <TokenPicker
          side={picker}
          chains={picker === "src" ? sourceChains : DESTINATION_CHAINS}
          assets={assets}
          current={picker === "src" ? { chain: srcKey, token: srcTokenAddr, deposit: false } : { chain: dstKey, token: dstToken.addr, deposit, asset: asset?.id }}
          src={src}
          owner={walletOf}
          rpcs={rpcs}
          onPick={(p) => pick(picker, p)}
          onClose={() => setPicker(null)}
        />
      )}
      {routeOpen && asset && (
        <RoutePicker
          asset={asset}
          src={src}
          current={dstKey}
          auto={routeAuto}
          quotes={routeQuotes}
          onAuto={() => setRouteAuto(true)}
          onPick={(chain, token) => {
            setRouteAuto(false);
            setDstKey(chain);
            setDstTokenAddr(token);
          }}
          onClose={() => setRouteOpen(false)}
        />
      )}
      {settingsOpen && (
        <Settings slippage={slippage} setSlippage={setSlippage} srcSlippage={srcSlippageStored} setSrcSlippage={setSrcSlippage} swapping={swapping} onClose={() => setSettingsOpen(false)} />
      )}
      {walletsOpen && (
        <Wallets
          focus={walletsOpen.focus}
          evmWallets={evmWallets}
          evm={evm}
          setEvm={setEvm}
          sol={sol}
          suiWallets={suiWallets}
          sui={sui}
          setSui={setSui}
          jupnetKey={jupnetKey}
          setJupnetKey={holdJupnetKey}
          jupnetSource={!!jupnetSrc}
          onKeyWarning={setKeyWarning}
          onClose={() => setWalletsOpen(null)}
        />
      )}
    </div>
  );
}

/** Further Jupnet stablecoins paid with the first (a USD basket): each with its amount, balance and
 *  a remove button, "Add another stablecoin" while there is room, and the total in USD. */
function Basket(p: {
  all: Token[];
  first: Token;
  rows: { token: Token; amount: string }[];
  balances: Record<string, bigint | null | undefined>;
  total: bigint | null;
  onChange: (rows: BasketRow[]) => void;
}) {
  const rows: BasketRow[] = p.rows.map((r) => ({ addr: r.token.addr, amount: r.amount }));
  const used = [{ addr: p.first.addr }, ...rows];
  const free = addable(p.all, used);
  const set = (i: number, r: Partial<BasketRow>) => p.onChange(rows.map((x, j) => (j === i ? { ...x, ...r } : x)));
  return (
    <div className="basket">
      {p.rows.map((r, i) => {
        const t = r.token;
        const bal = p.balances[t.addr];
        const choices = [t, ...addable(p.all, used)];
        return (
          <div className="basket-row" key={i}>
            <input
              className="amount basket-amount"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0"
              aria-label={`Amount of ${t.name ?? t.sym}`}
              value={r.amount}
              onChange={(e) => set(i, { amount: cleanAmount(e.target.value) })}
            />
            <select className="basket-token" value={t.addr} onChange={(e) => set(i, { addr: e.target.value })} aria-label="Stablecoin">
              {choices.map((c) => (
                <option key={c.addr} value={c.addr}>
                  {c.name ?? c.sym}
                </option>
              ))}
            </select>
            {typeof bal === "bigint" && (
              <button className="max-btn" disabled={bal === 0n} onClick={() => set(i, { amount: prettyMax(bal, t.decimals) })} title={`Your whole balance: ${prettyUnits(bal, t.decimals)}`}>
                Max
              </button>
            )}
            <button className="icon-btn basket-remove" onClick={() => p.onChange(rows.filter((_, j) => j !== i))} aria-label={`Remove ${t.name ?? t.sym}`}>
              ×
            </button>
          </div>
        );
      })}
      <div className="basket-foot">
        {used.length < BASKET_MAX && free.length > 0 && (
          <button className="link-btn" onClick={() => p.onChange([...rows, { addr: free[0]!.addr, amount: "" }])}>
            + Add another stablecoin
          </button>
        )}
        {p.total !== null && (
          <span className="basket-total">
            Total <strong>{prettyUnits(p.total, USD.decimals)} USD</strong>
          </span>
        )}
      </div>
    </div>
  );
}

/** "Max" as typed digits: the full amount, at most the token's decimals, trailing zeros dropped. */
function prettyMax(v: bigint, decimals: number): string {
  const s = v.toString().padStart(decimals + 1, "0");
  const whole = s.slice(0, s.length - decimals);
  const frac = s.slice(s.length - decimals).replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : whole;
}

/** A side's token and where it is; received on Jupnet, the token as Jupnet credits it (`sym`) and
 *  Jupnet as its chain (the route is a detail below). */
function TokenButton({ token, sym = token.sym, chain, deposit, onClick, label }: { token: Token; sym?: string; chain: Chain; deposit?: boolean; onClick: () => void; label: string }) {
  return (
    <button className={`token-btn${deposit ? " on-jupnet" : ""}`} onClick={onClick} aria-label={`${label}: ${sym} on ${deposit ? "Jupnet" : chain.label}`}>
      <TokenOnChain sym={sym} chain={deposit ? "jupnet" : chain.key} size={28} />
      <span className="token-btn-text">
        <span className="token-btn-sym">{sym}</span>
        <span className="token-btn-chain">{deposit ? "Jupnet" : (token.name ?? chain.label)}</span>
      </span>
      <IconChevron size={16} />
    </button>
  );
}

function CustomFields({ value, onChange, error, family }: { value: { addr: string; sym: string; decimals: string }; onChange: (v: { addr: string; sym: string; decimals: string }) => void; error: string | null; family: Family }) {
  return (
    <div className="custom">
      <label className="grow">
        <span>Token {family === "sui" ? "coin type" : "address"}</span>
        <input className="mono" value={value.addr} onChange={(e) => onChange({ ...value, addr: e.target.value })} placeholder={family === "evm" ? "0x…" : family === "sui" ? "0x…::module::NAME" : "mint address"} spellCheck={false} />
      </label>
      <label>
        <span>Symbol</span>
        <input value={value.sym} onChange={(e) => onChange({ ...value, sym: e.target.value })} />
      </label>
      <label>
        <span>Decimals</span>
        <input inputMode="numeric" value={value.decimals} onChange={(e) => onChange({ ...value, decimals: e.target.value })} aria-invalid={parseDecimals(value.decimals) === null} />
      </label>
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

function Recipient(p: {
  value: string;
  own?: string;
  touched: boolean;
  editing: boolean;
  family: Family;
  label: string;
  error: string | null;
  onEdit: () => void;
  onChange: (v: string) => void;
  onReset: () => void;
  onConnect: () => void;
}) {
  const mine = !!p.own && p.value === p.own;
  if (!p.editing && p.value)
    return (
      <div className="recipient">
        <span className="muted">{p.label}</span>
        <span className="recipient-val">
          <span className="mono">{short(p.value, 5)}</span>
          {mine && <span className="tag">{p.family === "jupnet" ? "your key" : "your wallet"}</span>}
          <button className="link-btn" onClick={p.onEdit}>
            Change
          </button>
        </span>
      </div>
    );
  return (
    <div className="recipient editing">
      <label htmlFor="recipient" className="muted">
        {p.label}
      </label>
      <input
        id="recipient"
        className="mono"
        value={p.value}
        onChange={(e) => p.onChange(e.target.value)}
        placeholder={p.family === "jupnet" ? "Jupnet account (base58)" : p.family === "evm" ? "0x…" : p.family === "solana" ? "Solana address" : "0x… (Sui address)"}
        spellCheck={false}
        autoComplete="off"
        aria-invalid={!!p.error}
        aria-describedby={p.error ? "recipient-err" : undefined}
      />
      {p.error && (
        <p id="recipient-err" className="field-error">
          {p.error.charAt(0).toUpperCase() + p.error.slice(1)}
        </p>
      )}
      <div className="recipient-actions">
        {p.own ? (
          !mine && (
            <button className="link-btn" onClick={p.onReset}>
              Use my {p.family === "jupnet" ? "Jupnet key" : "wallet"} ({short(p.own, 4)})
            </button>
          )
        ) : p.family === "jupnet" ? (
          <span className="fine">Paste the Jupnet account to credit</span>
        ) : (
          <button className="link-btn" onClick={p.onConnect}>
            Connect a {FAMILY_NAME[p.family]}
          </button>
        )}
      </div>
    </div>
  );
}

/** The quote's freshness: a ring filling until the next refresh. */
function RefreshRing({ paused }: { paused: boolean }) {
  const c = 2 * Math.PI * 7;
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" className={`ring${paused ? " paused" : ""}`} role="img" aria-label={paused ? "Quote paused" : "Quote refreshes automatically"}>
      <circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="2" />
      <circle cx="9" cy="9" r="7" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray={c} style={{ ["--c" as string]: c, animationDuration: `${QUOTE_REFRESH_MS}ms` }} className="ring-fill" transform="rotate(-90 9 9)" strokeLinecap="round" />
    </svg>
  );
}

const STEP_TEXT: Record<OpenPhase, (sym: string) => string> = {
  switch: () => "Network",
  build: () => "Prepare",
  approve: (sym) => `Approve ${sym}`,
  sign: () => "Confirm",
  submit: () => "Submit",
  confirm: () => "Land",
};

/** The open's steps so far: the current one active, the earlier ones done. */
function OpenSteps({ phases, sym }: { phases: OpenPhase[]; sym: string }) {
  return (
    <ol className="open-steps" aria-label="Swap steps">
      {phases.map((ph, i) => {
        const done = i < phases.length - 1;
        return (
          <li key={ph} className={done ? "done" : "active"}>
            <span className="open-step-dot" aria-hidden>
              {done ? "✓" : <Spinner size={12} />}
            </span>
            {STEP_TEXT[ph](sym)}
          </li>
        );
      })}
    </ol>
  );
}
